import React, { useState, useEffect } from "react";
import {
  Eye,
  EyeOff,
  LogIn,
  Mail,
  Lock,
  Shield,
  Smartphone,
  Phone,
  KeyRound,
  RotateCcw,
  ArrowLeft,
  CheckCircle2,
} from "lucide-react";
import { Turnstile } from '@marsidev/react-turnstile';

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [companyName, setCompanyName] = useState("Kerol Systems");
  const [companyLogo, setCompanyLogo] = useState("/logo.png");
  const [turnstileToken, setTurnstileToken] = useState(null);

  // 2FA Challenge States
  const [is2FaStep, setIs2FaStep] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [twoFactorMethod, setTwoFactorMethod] = useState("authenticator");
  const [availableMethods, setAvailableMethods] = useState([]);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [devOtpHint, setDevOtpHint] = useState("");

  useEffect(() => {
    fetchCompanySettings();

    const params = new URLSearchParams(window.location.search);
    if (params.get("suspended") === "1") {
      setError("Your account has been suspended. You have been automatically logged out.");
    } else if (params.get("timeout") === "1") {
      setError("Your session has timed out due to inactivity. Please sign in again.");
    }
  }, []);

  // Cooldown timer for resending OTP
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const fetchCompanySettings = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/general-settings`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        console.error("Failed to fetch general settings");
        return;
      }

      const result = await response.json();

      if (result.success && result.data) {
        setCompanyName(
          result.data.organization_name || "Kerol Systems"
        );

        if (result.data.company_logo_url) {
          let logoUrl = result.data.company_logo_url;
          if (!logoUrl.startsWith("http")) {
            const apiBase = import.meta.env.VITE_API_URL.replace("/api", "");
            logoUrl = `${apiBase}${logoUrl.startsWith("/") ? "" : "/"}${logoUrl}`;
          }
          setCompanyLogo(logoUrl);
        } else {
          setCompanyLogo("/logo.png");
        }
      }
    } catch (error) {
      console.error("Unable to load company settings", error);
      setCompanyLogo("/logo.png");
    }
  };

  // =========================
  // LOGIN SUBMIT (Step 1)
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!turnstileToken) {
      setError("Please complete the security check.");
      return;
    }

    try {
      setIsLoading(true);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            email,
            password,
            turnstileToken,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Invalid credentials");
        return;
      }

      // Check if 2FA challenge is required
      if (data.requires_2fa) {
        setIs2FaStep(true);
        setTwoFactorToken(data.two_factor_token);
        setTwoFactorMethod(data.method || "authenticator");
        setAvailableMethods(data.available_methods || ["authenticator", "email", "sms"]);
        setMaskedEmail(data.masked_email || "");
        setMaskedPhone(data.masked_phone || "");
        setTwoFactorCode("");
        setResendCooldown(30);
        if (data.dev_otp || data.otp) {
          setDevOtpHint(data.dev_otp || data.otp);
          setTwoFactorCode(data.dev_otp || data.otp);
        }
        return;
      }

      // Standard login success without 2FA
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("user", JSON.stringify(data.user));
      
      setEmail("");
      setPassword("");
      window.location.href = "/user-management-console";

    } catch (error) {
      console.error(error);
      setError("Server error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // =========================
  // VERIFY 2FA SUBMIT (Step 2)
  // =========================
  const handleVerify2Fa = async (e) => {
    e?.preventDefault();
    setError("");
    setResendMessage("");

    if (!twoFactorCode.trim()) {
      setError("Please enter the verification code.");
      return;
    }

    try {
      setIsLoading(true);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/login/2fa/verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            two_factor_token: twoFactorToken,
            code: twoFactorCode.trim(),
            method: twoFactorMethod,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || "Verification code is incorrect or expired.");
        return;
      }

      // 2FA Verification successful!
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("user", JSON.stringify(data.user));

      window.location.href = "/user-management-console";

    } catch (err) {
      console.error("Error verifying 2FA:", err);
      setError("Failed to verify code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // =========================
  // SWITCH 2FA METHOD
  // =========================
  const handleSwitchMethod = async (newMethod) => {
    setError("");
    setResendMessage("");
    setDevOtpHint("");
    setTwoFactorMethod(newMethod);
    setTwoFactorCode("");

    // If switching to email or sms, automatically trigger OTP send
    if (newMethod === "email" || newMethod === "sms") {
      try {
        setIsResending(true);
        const res = await fetch(`${import.meta.env.VITE_API_URL}/login/2fa/resend`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            two_factor_token: twoFactorToken,
            method: newMethod,
          }),
        });
        const d = await res.json();
        if (d.success) {
          setResendMessage(d.message || `Verification code sent via ${newMethod.toUpperCase()}`);
          setResendCooldown(45);
          if (d.dev_otp || d.otp) {
            setDevOtpHint(d.dev_otp || d.otp);
            setTwoFactorCode(d.dev_otp || d.otp);
          }
        } else {
          setError(d.message || "Could not send verification code.");
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsResending(false);
      }
    }
  };

  // =========================
  // RESEND 2FA OTP
  // =========================
  const handleResendOtp = async () => {
    if (resendCooldown > 0 || isResending) return;
    setError("");
    setResendMessage("");
    setDevOtpHint("");

    try {
      setIsResending(true);
      const res = await fetch(`${import.meta.env.VITE_API_URL}/login/2fa/resend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          two_factor_token: twoFactorToken,
          method: twoFactorMethod,
        }),
      });

      const d = await res.json();
      if (d.success) {
        setResendMessage(d.message || "New verification code sent!");
        setResendCooldown(45);
        if (d.dev_otp || d.otp) {
          setDevOtpHint(d.dev_otp || d.otp);
          setTwoFactorCode(d.dev_otp || d.otp);
        }
      } else {
        setError(d.message || "Failed to resend code.");
      }
    } catch (e) {
      setError("Failed to resend code. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      {/* Login Container */}
      <div className="w-full max-w-[430px]">

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl animate-in fade-in">
            <p className="text-red-600 dark:text-red-400 text-sm text-center font-medium">{error}</p>
          </div>
        )}

        {/* Resend Success Message */}
        {resendMessage && (
          <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl flex items-center justify-center gap-2 animate-in fade-in">
            <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
            <p className="text-emerald-700 dark:text-emerald-300 text-xs text-center font-medium">{resendMessage}</p>
          </div>
        )}

        {/* Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-7 sm:p-8 border border-slate-100 dark:border-gray-800">
          
          {/* ==================================================== */}
          {/* STEP 2: TWO-FACTOR AUTHENTICATION CHALLENGE          */}
          {/* ==================================================== */}
          {is2FaStep ? (
            <div>
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-inner">
                  <Shield className="w-7 h-7" />
                </div>
                <h1 className="text-xl font-bold text-slate-800 dark:text-gray-100">
                  Two-Factor Authentication
                </h1>
                <p className="text-slate-500 dark:text-gray-400 text-xs mt-1.5 leading-relaxed">
                  {twoFactorMethod === "authenticator" && (
                    <>Enter the 6-digit verification code generated by your <strong>Authenticator App</strong>.</>
                  )}
                  {twoFactorMethod === "email" && (
                    <>Enter the 6-digit code sent to your email <strong>{maskedEmail || "on file"}</strong>.</>
                  )}
                  {twoFactorMethod === "sms" && (
                    <>Enter the 6-digit SMS code sent to mobile <strong>{maskedPhone || "on file"}</strong>.</>
                  )}
                  {twoFactorMethod === "recovery_code" && (
                    <>Enter an unused 8-character <strong>Backup Recovery Code</strong>.</>
                  )}
                </p>
              </div>

              {/* 2FA Form */}
              <form onSubmit={handleVerify2Fa} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-slate-700 dark:text-gray-300 text-xs font-semibold block text-center">
                    {twoFactorMethod === "recovery_code" ? "Recovery Code (e.g. ABCD-1234)" : "6-Digit Security Code"}
                  </label>
                  <input
                    type="text"
                    inputMode={twoFactorMethod === "recovery_code" ? "text" : "numeric"}
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={twoFactorMethod === "recovery_code" ? 10 : 6}
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.toUpperCase())}
                    placeholder={twoFactorMethod === "recovery_code" ? "XXXX-XXXX" : "000000"}
                    className="w-full text-center tracking-[6px] text-2xl font-mono font-bold py-3 bg-slate-50 dark:bg-gray-800 border-2 border-slate-200 dark:border-gray-700 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                {/* Helper Badge for Generated OTP */}
                {devOtpHint && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700/80 rounded-xl space-y-1.5 animate-in fade-in">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900 dark:text-amber-200">
                        <KeyRound size={14} className="text-amber-600 shrink-0" />
                        <span>OTP Verification Code:</span>
                      </div>
                      <span className="px-2 py-0.5 font-mono text-sm font-bold tracking-[3px] bg-amber-200/90 dark:bg-amber-900 text-amber-900 dark:text-amber-100 rounded">
                        {devOtpHint}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-amber-800 dark:text-amber-300 pt-1 border-t border-amber-200 dark:border-amber-800">
                      <span>Code dispatched to your device</span>
                      <button
                        type="button"
                        onClick={() => setTwoFactorCode(devOtpHint)}
                        className="font-bold underline hover:text-amber-950 dark:hover:text-amber-100 cursor-pointer"
                      >
                        Auto-Fill Code
                      </button>
                    </div>
                  </div>
                )}

                {/* Resend button for Email / SMS */}
                {(twoFactorMethod === "email" || twoFactorMethod === "sms") && (
                  <div className="flex items-center justify-between text-xs pt-1">
                    <span className="text-slate-400">Didn't receive code?</span>
                    <button
                      type="button"
                      disabled={resendCooldown > 0 || isResending}
                      onClick={handleResendOtp}
                      className="text-blue-600 dark:text-blue-400 font-medium hover:underline disabled:text-slate-400 flex items-center gap-1"
                    >
                      <RotateCcw size={12} className={isResending ? "animate-spin" : ""} />
                      <span>{resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Code"}</span>
                    </button>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading || !twoFactorCode.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm mt-3"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Verify & Sign In</span>
                    </>
                  )}
                </button>

                {/* Switch Channel / Method Options */}
                <div className="pt-3 border-t border-slate-100 dark:border-gray-800">
                  <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider text-center mb-2">
                    Or verify another way
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {twoFactorMethod !== "authenticator" && (
                      <button
                        type="button"
                        onClick={() => handleSwitchMethod("authenticator")}
                        className="p-2 rounded-lg border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-800 flex items-center justify-center gap-1.5 text-slate-700 dark:text-gray-300 font-medium transition-colors"
                      >
                        <Smartphone size={14} className="text-blue-600" />
                        <span>Authenticator</span>
                      </button>
                    )}
                    {twoFactorMethod !== "email" && (
                      <button
                        type="button"
                        onClick={() => handleSwitchMethod("email")}
                        className="p-2 rounded-lg border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-800 flex items-center justify-center gap-1.5 text-slate-700 dark:text-gray-300 font-medium transition-colors"
                      >
                        <Mail size={14} className="text-emerald-600" />
                        <span>Email OTP</span>
                      </button>
                    )}
                    {twoFactorMethod !== "sms" && (
                      <button
                        type="button"
                        onClick={() => handleSwitchMethod("sms")}
                        className="p-2 rounded-lg border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-800 flex items-center justify-center gap-1.5 text-slate-700 dark:text-gray-300 font-medium transition-colors"
                      >
                        <Phone size={14} className="text-indigo-600" />
                        <span>Mobile SMS</span>
                      </button>
                    )}
                    {twoFactorMethod !== "recovery_code" && (
                      <button
                        type="button"
                        onClick={() => handleSwitchMethod("recovery_code")}
                        className="p-2 rounded-lg border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-800 flex items-center justify-center gap-1.5 text-slate-700 dark:text-gray-300 font-medium transition-colors"
                      >
                        <KeyRound size={14} className="text-amber-600" />
                        <span>Backup Code</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Cancel / Back to login */}
                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIs2FaStep(false);
                      setTwoFactorCode("");
                      setError("");
                    }}
                    className="text-xs text-slate-500 hover:text-slate-800 dark:hover:text-gray-200 flex items-center justify-center gap-1 mx-auto transition-colors"
                  >
                    <ArrowLeft size={13} />
                    <span>Back to email and password</span>
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* ==================================================== */
            /* STEP 1: REGULAR CREDENTIALS LOGIN                    */
            /* ==================================================== */
            <div>
              {/* Header */}
              <div className="text-center mb-7">
                {/* Company Logo */}
                <div className="flex justify-center mb-4">
                  <img
                    src={companyLogo || "/logo.png"}
                    alt={companyName}
                    className="h-20 w-auto object-contain"
                    onError={(e) => {
                      if (!e.currentTarget.src.endsWith("/logo.png")) {
                        e.currentTarget.src = "/logo.png";
                      }
                    }}
                  />
                </div>
                {/* Company Name */}
                <h1 className="text-2xl font-bold text-slate-800 dark:text-gray-100 tracking-tight">
                  {companyName}
                </h1>
                {/* Subtitle */}
                <p className="text-slate-500 dark:text-gray-400 text-sm mt-1.5">
                  Enter your credentials to access your account
                </p>
              </div>

              {/* FORM */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* EMAIL */}
                <div className="space-y-1">
                  <label className="text-slate-700 dark:text-gray-300 text-sm font-medium block">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Please enter your Email ID"
                      required
                      className="w-full pl-10 pr-3 py-2.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                {/* PASSWORD */}
                <div className="space-y-1">
                  <label className="text-slate-700 dark:text-gray-300 text-sm font-medium block">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Please enter your Password"
                      required
                      className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* TURNSTILE */}
                <div className="flex justify-center py-2">
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setTurnstileToken(token)}
                    options={{
                      theme: 'light',
                    }}
                  />
                </div>

                {/* BUTTON */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm mt-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      <span>Sign In</span>
                    </>
                  )}
                </button>

                {/* Forgot Password Link */}
                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={() => window.location.href = "/forgot-password"}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-all"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <span className="inline-block w-3.5 h-3.5 bg-blue-600 rounded-full" />
              CLOUDFLARE
            </span>
            <span className="text-slate-300">•</span>
            <button className="hover:text-slate-600 transition-colors">Privacy</button>
            <span className="text-slate-300">•</span>
            <button className="hover:text-slate-600 transition-colors">Help</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
